import React, { useEffect, useState } from 'react';

// Libraries/Packages
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';

//Hooks
import { useDynamicPageTitle } from '~/hooks/pageTitleHook';
import { useSelector, useDispatch } from 'react-redux';

//Actions
import { getUserData } from '~/redux/actions/auth-actions';
import { fetchAllProducts, fetchProductTypes } from '~/redux/actions/products-actions';
import { fetchCustomFieldsCatalog } from '~/redux/actions/customFields-actions';
import { getAllPaymentMethods } from '~/redux/actions/stripe-actions';
import { fetchGeneralSetting } from '~/redux/actions/general-actions';

// Utils
import { removeItem } from '~/utils/local-storage';
import {
  getUserToken,
  getResetToken,
  getDashboardMode,
  setDashboardMode,
  getImpersonationMode,
} from '~/utils/auth';

// Components
// import SignUp from '~/components/CreateAccount';
import SignUp from '~/components/CreateAccountNew';
import SignIn from '~/components/SignIn';
import ForgotPassword from '~/components/ForgotPassword';
import Verification from '~/components/Verification';
import AuthCallback from '~/components/AuthCallback';
import UpdatePassword from '~/components/UpdatePassword';
import Layout from '~/components/Layout';
import Dashboard from '~/components/Dashboard';
import Contacts from '~/components/Contacts';
import Templates from './components/Templates/Templates';
import ContactDetails from '~/components/Contacts/ContactDetails';
// import ImportContacts from '~/components/Contacts/ImportContacts';
import Orders from '~/components/Orders';
import GlobalSnackbar from '~/components/Snackbar';
import Account from '~/components/Account/Profile';
import User from '~/components/Account/User';
import Import from '~/components/Account/Import';
import Export from '~/components/Account/Export';
import UnverifiedEmail from '~/components/UnverifiedEmail';
import Plan from '~/components/Plan';
import CustomFields from '~/components/Settings/CustomFields';
// import CreateOrder from '~/components/Orders/CreateOrder';
import CreateOrderNew from '~/components/Orders/CreateOrderNew';
import ApiKeys from '~/components/Settings/ApiKeys';
import Wallet from '~/components/Account/Wallet';
import General from '~/components/Settings/General';
import Tags from '~/components/Settings/Tags';
import Webhooks from '~/components/Settings/Webhooks';
import WebhookDetails from '~/components/Settings/Webhooks/WebhookDetails';
import CreateOrEditWebhook from '~/components/Settings/Webhooks/CreateOrEditWebhook';
import WebhookLog from '~/components/Settings/Webhooks/WebhookLog';
import OrderDetailsNew from '~/components/Orders/OrderDetailsNew';
import OrderContacts from '~/components/Orders/OrderDetailsNew/Contacts';
import TemplateBuilder from '~/components/TemplateBuilder';
import ModeReminder from '~/components/ModeReminder';
import OfferPercentage from '~/components/OfferPercentage';
import Campaigns from '~/components/Campaigns';
import CreateCampaign from '~/components/Campaigns/CreateCampaigns';
import PhoneTracking from '~/components/PhoneTracking';
import ImportExport from './components/Account/ImportExport';
import SubCompanies from '~/components/Company/SubCompanies';
import CompanyProfile from '~/components/Company/CompanyProfile';
import ImportContacts from '~/components/Contacts/ImportContactsNew';
import HireDesigner from './components/Templates/HireDesigner';
import BestFit from './components/CreateAccountNew/BestFit';
import TransactionErrorModal from '~/components/Account/Wallet/TransactionErrorModal';
import TwoFactorVerificationModal from '~/components/TwoFactor/VerificationModal';
import TwoFactorSetupFlow from '~/components/TwoFactor/SetupFlow';
import TwoFactorReverificationModal from '~/components/TwoFactor/ReverificationModal';
import PersistentModalScrollbars from '~/components/General/PersistentModalScrollbars';

// Admin Routes
import AdminDashboard from '~/components/AdminPortal/Dashboard';
import AdminOrders from '~/components/AdminPortal/Orders';
import AdminUsers from '~/components/AdminPortal/Users';
import AdminBatches from '~/components/AdminPortal/Batches';
import AdminCategories from '~/components/AdminPortal/Categories';
import AdminLookups from '~/components/AdminPortal/Lookups';
import AdminDesignerQueries from '~/components/AdminPortal/DesignerQueries';
import AdminDesignerQueriesTemplateBuilder from '~/components/AdminPortal/DesignerQueries/TemplateBuilder';
import SecuritySettings from '~/components/AdminPortal/SecuritySettings';
import Postage from '~/components/AdminPortal/PricingControls/Postage';
import CostOfGood from '~/components/AdminPortal/PricingControls/CostOfGood';
import Pricing from '~/components/AdminPortal/PricingControls/Pricing';
import Plans from '~/components/AdminPortal/PricingControls/Plans';
import useQueryParams from './hooks/useQueryParams';
import { isOfferGeneratorWebsite } from './utils/helperFunctions';
import Wrong from './components/Errors/Wrong';
import QrCodes from './components/Engagement/QrCodes';
import QrCodeHistory from './components/Engagement/QrCodes/History';
import Purls from './components/Engagement/Purls';
import PurlHistory from './components/Engagement/Purls/History';

function App() {
  const navigate = useNavigate();
  const isOfferGenerator = isOfferGeneratorWebsite();
  const isAuthenticated = getUserToken();
  const currentPath = window?.location?.pathname;

  if (isOfferGenerator) {
    document.title = 'Offer Generator';
  }

  useEffect(() => {
    if (isOfferGenerator && isAuthenticated && !currentPath.includes('property-offers')) {
      navigate('/property-offers');
    }
  }, [isOfferGenerator, isAuthenticated, currentPath, navigate]);

  if (isOfferGenerator && !isAuthenticated) {
    return <Wrong />;
  }

  const [unVerifiedEmail, setUnVerifiedEmail] = useState(false);

  const [onBoardedCompleted, setOnBoardedCompleted] = useState(true);
  const [isInitialAppDataLoading, setIsInitialAppDataLoading] = useState(true);
  // chatbot widget
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);

  const { allSearchParams } = useQueryParams();

  const dispatch = useDispatch();

  let isResetPassword = getResetToken();

  // MiddleWare for protected routes
  function RequireClientAuth({ children, redirectTo }) {
    return isAuthenticated ? children : <Navigate to={redirectTo} />;
  }

  const isVerified = useSelector((state) => state.userReducers.user.status);

  const isOnboardingCompleted = useSelector((state) => state.userReducers?.user?.organization?.isOnboardingCompleted);

  const isAdminMode = JSON.parse(getDashboardMode()) || false;

  const isSystemAdmin = useSelector((state) => state.userReducers.isSystemAdmin);
  const isSystemSuperAdmin = useSelector((state) => state.userReducers.user?.isSystemSuperAdmin);

  // Payment failure global state — blocks all navigation when true
  const isPaymentFailedModalOpen = useSelector((state) => state.paymentFailureReducer?.isOpen);
  const shouldShowStartupModal =
    isAuthenticated && !isOfferGenerator && !isInitialAppDataLoading && !isPaymentFailedModalOpen;

  const isImpersonatedMode = getImpersonationMode() || false;

  useEffect(() => {
    if (currentPath.includes('admin') && !isAdminMode && !isImpersonatedMode) {
      setDashboardMode(true);
      navigate(currentPath);
    }
  }, [currentPath]);

  useEffect(() => {
    if (isSystemAdmin === false && currentPath.includes('admin')) {
      setDashboardMode(false);
      navigate('/overview');
    }
  }, [isSystemAdmin]);

  useEffect(() => {
    if (isSystemAdmin === true && isSystemSuperAdmin === false && currentPath.includes('admin/pricing-controls')) {
      navigate('/admin/overview');
    }
  }, [isSystemAdmin, isSystemSuperAdmin]);

  const getLayoutProps = () => {
    if (currentPath.includes('/account')) {
      return { isSideBar: true, name: 'account', isAdminMode };
    } else if (currentPath.includes('/contacts/import')) {
      return { isSideBar: true, name: 'account', isAdminMode };
    } else if (currentPath.includes('/my-company')) {
      return { isSideBar: true, isAdminMode };
    } else if (currentPath.includes('/sub-companies')) {
      // Use settings-style sidebar grouping for company management pages
      return { isSideBar: true, name: 'settings', isAdminMode };
    } else if (currentPath.includes('/settings')) {
      return { isSideBar: true, name: 'settings', isAdminMode };
    } else if (currentPath.includes('/webhooks')) {
      // Preserve the settings shell for legacy webhook detail links.
      return { isSideBar: true, name: 'settings', isAdminMode };
    } else if (currentPath.includes('/engagement')) {
      return { isSideBar: true, name: 'engagement', isAdminMode };
    }
    // else if (currentPath.includes('/orders/detail')) {
    //   return { isSideBar: true, name: 'order', isAdminMode };
    // }
    else if (currentPath.includes('/phone-tracking')) {
      return { isSideBar: true, name: 'phone-tracking', isAdminMode };
    } else {
      return { isSideBar: false, isAdminMode };
    }
  };

  // Dynamically Update page title
  useDynamicPageTitle();

  useEffect(() => {
    let isMounted = true;

    if (isAuthenticated && !isOfferGenerator) {
      setIsInitialAppDataLoading(true);

      Promise.allSettled([
        dispatch(getUserData()),
        dispatch(fetchProductTypes()),
        dispatch(fetchAllProducts()),
        dispatch(fetchCustomFieldsCatalog()),
        dispatch(fetchGeneralSetting()),
        dispatch(getAllPaymentMethods()),
      ]).finally(() => {
        if (isMounted) {
          setIsInitialAppDataLoading(false);
        }
      });

      setUnVerifiedEmail(isVerified === 'Pending' ? true : false);
    } else {
      setIsInitialAppDataLoading(true);
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  useEffect(()=>{
    if(typeof isOnboardingCompleted !== 'undefined'){
      setOnBoardedCompleted(isOnboardingCompleted);
    }
  }, [isOnboardingCompleted])

  useEffect(() => {
    if (isSystemAdmin !== null && isSystemAdmin === false) {
      removeItem('isAdminMode');
    }
  }, [isAdminMode]);

  // Hubspot Chatbot display
  const handleChatOpen = () => {
    const chatbot = document.getElementById('hubspot-messages-iframe-container');
    if (chatbot) {
      chatbot?.style.setProperty('display', 'initial', 'important');
      chatbot?.style.setProperty('box-shadow', '0px 0px 9px 2px #0000002e', 'important');
      chatbot?.style.setProperty('border-radius', '20px', 'important');
      chatbot?.style.setProperty('background', '#00000024', 'important');
    }
    setIsWidgetVisible(true);
    window?.HubSpotConversations?.widget?.load({ widgetOpen: false });
    window?.HubSpotConversations?.widget?.open();
  };

  useEffect(() => {
    const hideChatbot = () => {
      const chatbot = document.getElementById('hubspot-messages-iframe-container');
      if (chatbot) {
        chatbot?.style.setProperty('display', 'none', 'important');
        setIsWidgetVisible(false);
      } else {
        setTimeout(hideChatbot, 100);
      }
    };
    hideChatbot();

    const handleWidgetClosed = (e) => {
      setTimeout(() => {
        hideChatbot();
      }, 100);
    };

    window?.HubSpotConversations?.on('widgetClosed', handleWidgetClosed);

    return () => {
      window?.HubSpotConversations?.off('widgetClosed', handleWidgetClosed);
    };
  }, []);
  // Hubspot Chatbot display

  return (
    <>
      <PersistentModalScrollbars />
      <Routes>
        {!isAuthenticated && (
          <>
            <Route path="/login" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/reset-password" element={<ForgotPassword />} />
          </>
        )}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verification" element={<Verification />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route
          path="/"
          element={
            <Navigate
              to={isAdminMode ? `admin/overview${allSearchParams}` : `overview${allSearchParams}`}
            />
          }
        ></Route>
        <Route
          element={
            isAuthenticated ? (
              <React.Fragment>
                <Layout
                  {...getLayoutProps()}
                  onClick={handleChatOpen}
                  isWidgetVisible={isWidgetVisible}
                  setIsWidgetVisible={setIsWidgetVisible}
                  hideHeader={isOfferGenerator}
                />
                {onBoardedCompleted && !isOfferGenerator && shouldShowStartupModal && (
                  <UnverifiedEmail
                    isOpen={unVerifiedEmail}
                    handleCloseModal={() => setUnVerifiedEmail(false)}
                  />
                )}
                {
                  !onBoardedCompleted && shouldShowStartupModal &&  <BestFit isOpen={!onBoardedCompleted}/>
                }
              </React.Fragment>
            ) : (
              <Navigate to={`/login${allSearchParams}`} />
            )
          }
        >
          {/* Add Next Auth Route Here*/}
          {!isAdminMode && (
            <>
              <Route path="overview" element={<Dashboard />} />
              <Route path="contacts" element={<Contacts />}></Route>
              <Route path="contacts/:id" element={<ContactDetails />}></Route>
              <Route path="contacts/import" element={<ImportContacts />}></Route>
              <Route path="campaigns" element={<Campaigns />}></Route>
              <Route path="campaigns/create" element={<CreateCampaign />}></Route>
              <Route path="campaigns/edit/:id" element={<CreateCampaign />}></Route>
              <Route path="templates" element={<Templates />} />
              <Route path="create-template" element={<TemplateBuilder />} />
              <Route path="template-builder/:id?" element={<TemplateBuilder />} />
              {/* <Route path="html-template-builder/:id?" element={<TemplateBuilder builderMode="html" />} /> */}
              <Route path="hire-a-designer" element={<HireDesigner />} />
              <Route
                path="hire-a-designer/proof/:id"
                element={<TemplateBuilder approvalMode />}
              />
              <Route path="orders" element={<Orders />}></Route>
              {/* <Route path="orders/create-order" element={<CreateOrder />}></Route> */}
              <Route path="orders/create-order" element={<CreateOrderNew />}></Route>
              {/* order detail rooutes */}
              <Route path="orders/detail/overview/:id" element={<OrderDetailsNew />} />
              <Route path="orders/detail/contacts/:id" element={<OrderContacts />}></Route>
              {/* Company */}
              <Route path="company/sub-companies" element={<SubCompanies />}></Route>
              {/* <Route path="company/sub-companies/:id" element={<CompanyProfile />}></Route> */}
              <Route path="my-company" element={<CompanyProfile />}></Route>
              <Route path="account/imports-exports" element={<ImportExport />}></Route>
              {/* Account Route*/}
              <Route path="/account" element={<Navigate to="/account/profile" />}></Route>
              <Route path="account/profile" element={<Account />}></Route>
              <Route path="account/wallet" element={<Wallet />}></Route>
              <Route path="account/plan" element={<Plan />}></Route>
              <Route path="account/users" element={<User />}></Route>
              <Route path="account/imports" element={<Import />}></Route>
              <Route path="account/exports" element={<Export />}></Route>
              {/* Account Settngs*/}
              <Route path="/settings" element={<Navigate to="/settings/api-keys" />}></Route>
              <Route path="settings/api-keys" element={<ApiKeys />}></Route>

              <Route path="account/wallet" element={<Wallet />}></Route>
              <Route path="account/plan" element={<Plan />}></Route>

              <Route path="account/users" element={<User />}></Route>

              {/* Account Settngs*/}
              <Route path="/settings" element={<Navigate to="/settings/general" />}></Route>
              <Route path="settings/general" element={<General />}></Route>
              <Route path="settings/custom-fields" element={<CustomFields />}></Route>
              <Route path="settings/api-keys" element={<ApiKeys />}></Route>
              <Route path="settings/tags" element={<Tags />}></Route>
              <Route path="settings/webhooks" element={<Webhooks />}></Route>
              <Route path="settings/webhooks/:id" element={<WebhookDetails />}></Route>
              <Route path="settings/webhooks/:key/webhook-log/:id" element={<WebhookLog />}></Route>
              <Route path="webhooks/:id" element={<WebhookDetails />}></Route>
              <Route path="webhooks/:key/webhook-log/:id" element={<WebhookLog />}></Route>
              <Route path="settings/webhooks/create" element={<CreateOrEditWebhook />}></Route>
              <Route path="settings/webhooks/update/:id" element={<CreateOrEditWebhook />}></Route>
              {/* offer percentage */}
              <Route path="property-offers" element={<OfferPercentage />}></Route>
              {/* phone tracking */}
              <Route path="phone-tracking" element={<PhoneTracking />}></Route>
              <Route path="phone-tracking/:id/:type" element={<PhoneTracking />}></Route>

              {/* Engagement */}
              <Route path="/engagement" element={<Navigate to="/phone-tracking" />}></Route>
              <Route path="/engagement/qr-codes" element={<QrCodes />}></Route>
              <Route path="/engagement/qr-code-history/:id" element={<QrCodeHistory />}></Route>
              <Route path="/engagement/purls" element={<Purls />}></Route>
              <Route path="/engagement/purl-history/:id" element={<PurlHistory />}></Route>
            </>
          )}

          {isAdminMode && (
            <>
              <Route path="admin/overview" element={<AdminDashboard />} />
              <Route path="admin/orders/:orderId?" element={<AdminOrders />} />
              <Route path="admin/orders/batch/:batchId" element={<AdminOrders />} />
              <Route path="admin/users/:userId?" element={<AdminUsers />} />
              <Route path="admin/batches/:batchId?" element={<AdminBatches />} />
              <Route path="admin/categories" element={<AdminCategories />} />
              <Route path="admin/lookups" element={<AdminLookups />} />
              <Route
                path="admin/designer-queries/:designerQueryId?"
                element={<AdminDesignerQueries />}
              />
              <Route
                path="admin/designer-queries/template/:id"
                element={<AdminDesignerQueriesTemplateBuilder />}
              />
              <Route path="admin/security-settings" element={<SecuritySettings />} />
            </>
          )}

          {isAdminMode && isSystemSuperAdmin !== false && (
            <>
              <Route path="admin/pricing-controls/postage" element={<Postage />} />
              <Route path="admin/pricing-controls/cost-of-good" element={<CostOfGood />} />
              <Route path="admin/pricing-controls/pricing" element={<Pricing />} />
              <Route path="admin/pricing-controls/plans" element={<Plans />} />
            </>
          )}
        </Route>
        <Route path="*" element={<Navigate to={`/${allSearchParams}`} />}></Route>
      </Routes>

      {/* SNACKBAR FOR NOTIFICATIONS */}
      <GlobalSnackbar />
      {/* Mode Reminder */}
      {!isAdminMode && <ModeReminder />}
      {/* ── Payment Failure Blocking Modal ──────────────────────────────────────
          Rendered at the root level so it overlays the entire app.
          Only shown when the Axios interceptor detects payment-failed: true
          in any API response header. Cannot be dismissed by the user.
      ──────────────────────────────────────────────────────────────────────── */}
      {isAuthenticated && <TransactionErrorModal open={!!isPaymentFailedModalOpen} />}
      {/* Two-Factor Authentication Modals */}
      <TwoFactorVerificationModal />
      <TwoFactorSetupFlow />
      {isAuthenticated && <TwoFactorReverificationModal />}
    </>
  );
}

export default App;
